/**
 * The Approve / Decline controls on one proposal card.
 *
 * Rendered only for the three approvers and for People Space admins acting on their behalf —
 * everyone else sees the verdicts and no buttons. ⚠️ That is COSMETIC. The proxy re-decides who may
 * record a decision on every request (`roleApprovals.decisionRefusal`), because anyone who can open
 * this page can call the route directly. Hiding the buttons is courtesy, not access control.
 */
import { useRef, useState } from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Textarea } from '@finn-auto/design-system/atoms/textarea';
import type { ApprovalDecision, ApproverKey, ApproverState } from '../../types/roleApprovals';

/** Long enough for a real reason, and the same ceiling the proxy trims to. */
const MAX_REASON = 500;

interface Props {
  approver: ApproverState;
  /** True when the signed-in person may sign for this office; false when an admin is recording
   * for them. */
  isSelf: boolean;
  /** True when they hold that sign-off as a DELEGATE rather than as the officeholder. */
  isDelegate: boolean;
  disabled: boolean;
  onDecide: (approverKey: ApproverKey, decision: ApprovalDecision, reason?: string) => void;
  /** "Approve all 2" on a bulk submission's card; plain "Approve" / "Decline" everywhere else. */
  approveLabel?: string;
  declineLabel?: string;
}

export function ApprovalControls({
  approver,
  isSelf,
  isDelegate,
  disabled,
  onDecide,
  approveLabel = 'Approve',
  declineLabel = 'Decline',
}: Props) {
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState('');
  // Opening the form unmounts the Decline button the person just pressed, which drops focus to
  // <body>. Closing it puts focus back where it came from rather than at the top of the document.
  const declineTrigger = useRef<HTMLButtonElement>(null);

  const decided = approver.decision !== null;

  const closeForm = () => {
    setDeclining(false);
    setReason('');
    // After the re-render that brings the button back.
    requestAnimationFrame(() => declineTrigger.current?.focus());
  };

  if (declining) {
    return (
      <form
        // `basis-full` because this sits in the card's `justify-between` row: without a width the
        // box stays at the textarea's intrinsic width at every screen size.
        className="flex basis-full flex-col gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          if (disabled) return;
          onDecide(approver.key, 'Declined', reason);
          closeForm();
        }}
      >
        <Textarea
          label="Why is this being declined?"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder="Budget is committed elsewhere this trimester"
          rows={2}
          maxLength={MAX_REASON}
        />
        <div className="flex gap-2">
          <Button type="submit" size="sm" variant="primary" disabled={disabled}>
            Confirm decline
          </Button>
          <Button type="button" size="sm" variant="secondary" onClick={closeForm}>
            Cancel
          </Button>
        </div>
        {/* The reason is optional, and saying so stops a decline stalling behind a blank box. It
            is worth asking for all the same: the proposer reads this and nothing else explains a
            no. */}
        <p className="text-xs text-zinc-500">Optional, but it is what the proposer will read.</p>
      </form>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {!isSelf && (
        // Stated before the click, not after. An admin recording someone else's decision is a
        // legitimate and useful thing to do — and it is permanently attributed to them in the
        // audit log, so they should know that while deciding rather than discover it later.
        <span className="text-xs text-amber-700">
          Recording for {approver.name} — logged under your name.
        </span>
      )}
      {isSelf && isDelegate && (
        // A delegate holds this office's sign-off without being its holder, so the row's name is
        // somebody else's. Saying it here is the point: approving means this office has signed
        // and {approver.name} has not necessarily seen it. The audit line records the same thing
        // permanently — see the `APPROVERS` warning in the proxy.
        <span className="text-xs text-amber-700">
          You sign for {approver.name} here — your name is recorded on the decision.
        </span>
      )}
      {approver.decision !== 'Approved' && (
        <Button
          size="sm"
          variant="primary"
          disabled={disabled}
          onClick={() => onDecide(approver.key, 'Approved')}
        >
          {approveLabel}
        </Button>
      )}
      {approver.decision !== 'Declined' && (
        <Button
          ref={declineTrigger}
          size="sm"
          variant="secondary"
          disabled={disabled}
          onClick={() => setDeclining(true)}
        >
          {declineLabel}
        </Button>
      )}
      {decided && (
        // Withdrawal is what makes a decline reversible, and it is the only route back: the proxy
        // derives the status from the three cells, so clearing one returns the proposal to
        // `Proposed` with no separate transition to call.
        <Button
          size="sm"
          variant="secondary"
          disabled={disabled}
          onClick={() => onDecide(approver.key, null)}
        >
          Withdraw
        </Button>
      )}
    </div>
  );
}
